
import Vue from "vue";
import {mapState} from "vuex";
import {Article, Dossier, PackageInfo} from "@/api/flame";
import draggable from "vuedraggable";
import {findConfirmDialogRef} from "@/utils/RefUtils";
import {Product} from "@/api/products";
import {Pallet} from "@/api/pallet";
import EventBus from "@/utils/EventBus";
import {SnackbarMsgEvent} from "@/layout/Layout.vue";

export interface PackageFormInputDTO {
  dossier: Dossier | null;
}

let packageKeyCounter = 0;


export default Vue.extend({
  components: {
    draggable,
  },

  props: {
    packageFormInput: {
      type: Object as () => PackageFormInputDTO,
      required: true,
    },
    isWorking: {
      type: Boolean,
    },
  },
  data() {
    return {
      readonly: false,
      possiblePallet: null as Pallet | null,
      packages: [] as PackageInfo[],
      selectedPackages: [] as PackageInfo[],
      packageKeys: new WeakMap<PackageInfo, string>(),
      selectedIndex: 0,
      headersPackageColumns: [
        {
          text: "",
          sortable: false,
          width: "48px",
          value: "selection",
        },
        {
          text: this.$t("Menge"),
          sortable: false,
          width: "5%",
          value: "count",
        },
        {
          text: this.$t("Kistenmasse ISPM BTH cm"),
          sortable: false,
          width: "10%",
        },
        {
          text: this.$t("Gewicht kg FR inkl. RS (2kg) & Kiste/Palette kg"),
          sortable: false,
          width: "10%",
        },
        {
          text: this.$t("Info"),
          sortable: false,
          width: "30%",
        },
        {
          text: this.$t("Aktion"),
          sortable: false,
          width: "80px",
        },
     ],
    }
  },
  watch: {
    packageFormInput: {
      handler() {
        const packageInfos = this.packageFormInput.dossier?.packageInfos ?? [];
        this.packages = [...packageInfos].sort(
            (a: PackageInfo, b: PackageInfo) =>
                (a.orderKey ?? Number.MAX_SAFE_INTEGER) - (b.orderKey ?? Number.MAX_SAFE_INTEGER)
        );
        this.selectedPackages = this.selectedPackages.filter((item: PackageInfo) =>
            this.packages.includes(item)
        );
      },
      immediate: true,
    },
    possiblePallet: {
      handler() {
        if (this.possiblePallet !== null) {
          this.packages[this.selectedIndex].widthInCm = this.possiblePallet.widthInCm;
          this.packages[this.selectedIndex].heightInCm = this.possiblePallet.heightInCm;
          this.packages[this.selectedIndex].depthInCm = this.possiblePallet.depthInCm;
          this.packages[this.selectedIndex].weightInKg = this.possiblePallet.weightInKg;
        }
      },
      immediate: true,
    },
  },
  async created() {
    await this.$store.dispatch("pallet/listAll");
    await this.$store.dispatch("product/listAll");
  },
  computed: {
    ...mapState("pallet", ["pallets"]),
    ...mapState("product", ["productsOnSale"]),
  },
  methods: {
    allPackagesSelected(): boolean {
      return this.packages.length > 0 && this.selectedPackages.length === this.packages.length;
    },
    somePackagesSelected(): boolean {
      return this.selectedPackages.length > 0 && !this.allPackagesSelected();
    },
    formatPackageInfo(item: PackageInfo): string {
      return `BTH ${item.widthInCm} x ${item.depthInCm} x ${item.heightInCm} cm, ${item.weightInKg} kg`;
    },
    packageKey(item: PackageInfo): string {
      // Vuetify's VInput watches its `value` prop and copies it into the internal lazyValue.
      // With index keys a checkbox is reused for a different package after a drag, so its
      // model becomes the package itself and every row appears selected. A stable key per
      // package object keeps each checkbox bound to its own item.
      if (!this.packageKeys.has(item)) {
        this.packageKeys.set(item, `package-${++packageKeyCounter}`);
      }
      return this.packageKeys.get(item) as string;
    },
    writeClipboard(text: string, successMessage: string) {
      navigator.clipboard.writeText(text).then(function() {
        EventBus.$emit("snackbarInfosTo", { msg: successMessage } as SnackbarMsgEvent);
      }, function(err) {
        EventBus.$emit("snackbarInfosTo",  { msg:"Kistenmasse + Gewicht konnte nicht kopiert werden "+err } as SnackbarMsgEvent);
      });
    },
    async copyToClipboard(index: number) {
      if (this.selectedPackages.length > 0) {
        this.copySelectedToClipboard();
        return;
      }
      this.writeClipboard(
          this.formatPackageInfo(this.packages[index]),
          "Kistenmasse + Gewicht erfolgreich kopiert"
      );
    },
    copySelectedToClipboard() {
      const selected = this.packages.filter((item: PackageInfo) =>
          this.selectedPackages.includes(item)
      );
      const message = selected.length === 1
          ? "Kistenmasse + Gewicht erfolgreich kopiert"
          : `Kistenmasse + Gewicht von ${selected.length} Packstücken erfolgreich kopiert`;
      this.writeClipboard(
          selected.map((item: PackageInfo) => this.formatPackageInfo(item)).join("\n"),
          message
      );
    },
    toggleSelectAllPackages() {
      this.selectedPackages = this.allPackagesSelected() ? [] : [...this.packages];
    },
    normalizePackageOrder() {
      this.packages.forEach((item: PackageInfo, index: number) => {
        if (item) {
          item.orderKey = index + 1;
        }
      });
    },
    async removePackageInfo(index: number) {
      const res = await findConfirmDialogRef(this).open(
          this.$t("Achtung") as string,
          this.$t("Wirklich das Packstück entfernen?") as string,
          {
            textColor: "red",
            textOk: "Ja",
            textCancel: "Nein und zurück",
            secondOkButton: false,
            noconfirm: false,
          }
      );
      if (res) {
        const [removed] = this.packages.splice(index, 1);
        this.selectedPackages = this.selectedPackages.filter((item: PackageInfo) => item !== removed);
        this.normalizePackageOrder();
        this.$emit("packageInfoChanged", this.packages);
      }
    },
    addEmptyPackageInfo() {
      if (!this.packages) {
        this.packages = [];
      }
      this.packages.push({
        quantity: 1,
        version: 1,
        orderKey: this.packages.length + 1,
        description: null,
        id: null,
        weightInKg: 0,
        widthInCm: 0,
        depthInCm: 0,
        heightInCm: 0,
      });
      this.normalizePackageOrder();
      this.$emit("packageInfoChanged", this.packages);
    },
    updatePackageInfo() {
      this.normalizePackageOrder();
      this.$emit("packageInfoChanged", this.packages);
    },
    buildPalletTitle(item?: Pallet) {
      if( item ) {
        const {id, widthInCm, heightInCm, depthInCm, weightInKg, description } = item;
        return new Pallet(id, widthInCm, heightInCm, depthInCm, weightInKg, false, description).buildTitle();
      }
      return "";
    },
    customPalletFilter (item:Pallet, queryText:string): boolean {
      const {id, widthInCm, heightInCm, depthInCm, weightInKg, description } = item;
      const textOne = new Pallet(id, widthInCm, heightInCm, depthInCm, weightInKg, false, description).buildTitle().toLowerCase();
      const searchText = queryText.toLowerCase()
      return textOne.indexOf(searchText) > -1
    },
    async calcPackageInfo() {
      const res = await findConfirmDialogRef(this).open(
          this.$t("Achtung") as string,
          this.$t("Wirklich Packstücke zurücksetzen?") as string,
          {
            textColor: "red",
            textOk: "Ja",
            textCancel: "Nein und zurück",
            secondOkButton: false,
            noconfirm: false,
          }
      );
      if( res ) {
        this.packages = []
        this.selectedPackages = [];
      }
      if ( res && this.packageFormInput.dossier?.articles) {
           this.packageFormInput.dossier?.articles.forEach((article: Article) => {
           const product = this.findByProductName(article.productName);
           if( product && product.possiblePallet ) {

               Array(article.quantity).fill(0).forEach(() => {
                 this.packages.push({
                   quantity: 1,
                   version: 1,
                   widthInCm: product.possiblePallet?.widthInCm ?? 0,
                   heightInCm: product.possiblePallet?.heightInCm ?? 0,
                   depthInCm: product.possiblePallet?.depthInCm ?? 0,
                   weightInKg: (product.possiblePallet?.weightInKg ?? 0) + (product.weightInKg ?? 0),
                   description: product.title,
                   id: null,
                   orderKey: this.packages.length + 1,
                 })
               })
           }
         })
      }
      if( res ) {
        this.normalizePackageOrder();
      }
      this.$emit("packageInfoChanged", this.packages);
    },
    findByProductName(productName: string): Product | undefined {
      return this.productsOnSale.find((product: Product) => product.title === productName)
    },
    movePackageInfo(evt: { moved?: { oldIndex: number; newIndex: number } }) {
      if (!evt.moved) {
        return;
      }
      // vuedraggable already reordered this.packages; persist the new order as 1..n
      this.normalizePackageOrder();
      this.$emit("packageInfoChanged", this.packages);
    },
  },
});
